import {
  recordCipherFontLoadFailure,
  recordCipherFontLoadSummary,
} from './cipherFontLoadLog';
import { registerCipherFontRules } from './cipherFontRegistry';

/** 密文字体 CDN 根路径（不含末尾斜杠）。须在微信 downloadFile 合法域名下（www.zh-h.cn） */
export const DEFAULT_CIPHER_FONT_CDN_BASE = 'https://www.zh-h.cn/static/fonts/cipher';

/** 开发/预览环境 CDN（xuewen.zh-h.cn，同为微信 downloadFile 合法域名） */
export const DEV_CIPHER_FONT_CDN_BASE = 'https://xuewen.zh-h.cn/static/fonts/cipher';

/** 本地 Django 静态目录（start-dev.sh 默认） */
export const LOCAL_CIPHER_FONT_CDN_BASE = 'http://127.0.0.1:8000/static/fonts/cipher';

/** syncCipherFonts / 历史 CSS 中可能出现过的 CDN 根路径 */
export const KNOWN_CIPHER_FONT_CDN_BASES = [
  DEFAULT_CIPHER_FONT_CDN_BASE,
  DEV_CIPHER_FONT_CDN_BASE,
  LOCAL_CIPHER_FONT_CDN_BASE,
  'http://localhost:8000/static/fonts/cipher',
  '/static/fonts/cipher',
] as const;

export const CIPHER_MP_FONT_FACES_CSS = 'cipher-mp-font-faces.css';
export const CIPHER_WEB_FONT_FACES_CSS = 'cipher-web-font-faces.css';

export interface ParsedFontFaceRule {
  family: string;
  sourceUrl: string;
  unicodeRange?: string;
}

/** 将 CSS 内所有已知 CDN 根路径替换为目标 base（woff2 相对路径不变） */
export function rewriteFontCssUrls(css: string, targetBase: string): string {
  const normalized = targetBase.replace(/\/$/, '');
  let result = css;

  // Repair stacked scheme+host prefixes before /static/fonts/cipher/ (from prior faulty rewrites).
  result = result.replace(
    /(?:https?:\/\/[^/\s"']+)+(\/static\/fonts\/cipher\/)/g,
    () => `${normalized}/`,
  );

  const absoluteBases = KNOWN_CIPHER_FONT_CDN_BASES.filter(
    (base) => base.startsWith('http://') || base.startsWith('https://'),
  )
    .filter((base) => base !== normalized)
    .sort((a, b) => b.length - a.length);

  for (const base of absoluteBases) {
    result = result.split(base).join(normalized);
  }

  // Only rewrite relative url("/static/fonts/cipher/…") paths — not /static/ inside absolute URLs.
  if (normalized !== '/static/fonts/cipher') {
    result = result.replace(
      /url\(\s*(["'])\/static\/fonts\/cipher\//g,
      (_match, quote: string) => `url(${quote}${normalized}/`,
    );
    result = result.replace(
      /url\(\s*\/static\/fonts\/cipher\//g,
      () => `url(${normalized}/`,
    );
  }

  return result;
}

/** 解析 @font-face src 中的字体 URL（去掉 format() 等后缀） */
export function parseFontFaceSourceUrl(srcValue: string): string | null {
  const quoted = srcValue.match(/url\(\s*["']([^"']+)["']\s*\)/);
  if (quoted) return quoted[1].trim();
  const bare = srcValue.match(/url\(\s*([^)\s]+)\s*\)/);
  return bare ? bare[1].trim().replace(/^["']|["']$/g, '') : null;
}

/** 将 @font-face src 解析为当前 CDN base 下的可请求 URL */
export function resolveFontFaceUrl(sourceUrl: string, cdnBase: string): string {
  const trimmed = sourceUrl.trim();
  const base = cdnBase.replace(/\/$/, '');

  const relativeFromKnownBase = (url: string): string | null => {
    for (const known of KNOWN_CIPHER_FONT_CDN_BASES) {
      const prefix = `${known.replace(/\/$/, '')}/`;
      if (url.startsWith(prefix)) {
        return url.slice(prefix.length);
      }
    }
    const marker = '/static/fonts/cipher/';
    const idx = url.indexOf(marker);
    if (idx >= 0) {
      return url.slice(idx + marker.length);
    }
    return null;
  };

  if (/^https?:\/\//i.test(trimmed)) {
    const rel = relativeFromKnownBase(trimmed);
    return rel ? `${base}/${rel}` : trimmed;
  }

  const basePrefix = `${base}/`;
  if (trimmed.startsWith(basePrefix)) {
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }
  if (trimmed === base) {
    return base.startsWith('/') ? base : `/${base}`;
  }

  const rel = relativeFromKnownBase(trimmed);
  if (rel) {
    return `${base}/${rel}`;
  }

  const path = trimmed.replace(/^\/+/, '');
  return `${base}/${path}`;
}

/** 从 @font-face CSS 文本解析字体规则（供小程序 loadFontFace 使用） */
export function parseFontFaceRulesFromCss(css: string): ParsedFontFaceRule[] {
  const rules: ParsedFontFaceRule[] = [];
  for (const block of css.split('@font-face')) {
    if (!block.includes('font-family')) continue;
    const familyMatch = block.match(/font-family:\s*["']([^"']+)["']/);
    const srcLineMatch = block.match(/src:\s*([^;]+);/);
    if (!familyMatch || !srcLineMatch) continue;
    const sourceUrl = parseFontFaceSourceUrl(srcLineMatch[1]);
    if (!sourceUrl) continue;
    const rangeMatch = block.match(/unicode-range:\s*([^;]+);/);
    rules.push({
      family: familyMatch[1],
      sourceUrl,
      unicodeRange: rangeMatch?.[1]?.trim(),
    });
  }
  return rules;
}

/**
 * 由各前端 vite.config 通过 define 注入，避免小程序运行时解析 import.meta.env
 *（会错误拉取 Node url 模块导致白屏）。
 */
declare const __CIPHER_FONT_CDN_BASE__: string | undefined;

/**
 * Admin 本地开发时，Vite 通过 /static 代理到 Django。
 * 若仍用 http://127.0.0.1:8000 拉字体，会与 localhost:13000 跨域（CORS 失败）。
 */
function resolveWebCipherFontCdnBase(configured: string): string {
  const base = configured.replace(/\/$/, '');
  if (typeof window === 'undefined') return base;

  const { hostname, port, protocol } = window.location;
  const isLocalDevHost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isDevServer = isLocalDevHost && port !== '8000' && protocol.startsWith('http');

  if (
    isDevServer &&
    (base.startsWith('http://127.0.0.1:') ||
      base.startsWith('http://localhost:') ||
      base === LOCAL_CIPHER_FONT_CDN_BASE.replace(/\/$/, '') ||
      base === 'http://localhost:8000/static/fonts/cipher')
  ) {
    return '/static/fonts/cipher';
  }
  return base;
}

export function getCipherFontCdnBase(): string {
  const configured =
    typeof __CIPHER_FONT_CDN_BASE__ !== 'undefined' ? __CIPHER_FONT_CDN_BASE__ : '';
  if (configured) {
    return resolveWebCipherFontCdnBase(configured);
  }
  return DEFAULT_CIPHER_FONT_CDN_BASE;
}

export function cipherFontCdnUrl(relativePath: string): string {
  const path = relativePath.replace(/^\/+/, '');
  return `${getCipherFontCdnBase()}/${path}`;
}

export function getCipherMpFontFacesCssUrl(): string {
  return cipherFontCdnUrl(CIPHER_MP_FONT_FACES_CSS);
}

export function getCipherWebFontFacesCssUrl(): string {
  return cipherFontCdnUrl(CIPHER_WEB_FONT_FACES_CSS);
}

/** 管理后台 / H5：注入 CDN @font-face 样式表（link 方式，不 rewrite） */
export function injectCipherFontStylesheet(cssUrl: string): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector('[data-cipher-fonts]')) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = cssUrl;
  link.setAttribute('data-cipher-fonts', 'true');
  document.head.appendChild(link);
}

/**
 * 管理后台：拉取 @font-face CSS 并按当前 getCipherFontCdnBase() 重写 woff2 URL 后注入。
 * 失败仅记录 log，不阻断页面渲染。
 */
export async function injectCipherFontFaces(): Promise<void> {
  if (typeof document === 'undefined') return;

  const cdnBase = getCipherFontCdnBase();
  const cssUrl = getCipherWebFontFacesCssUrl();

  let css: string;
  try {
    const res = await fetch(cssUrl);
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    css = rewriteFontCssUrls(await res.text(), cdnBase);
  } catch (err) {
    recordCipherFontLoadFailure({
      kind: 'css_fetch',
      platform: 'web',
      message: `Failed to fetch cipher font CSS`,
      url: cssUrl,
      detail: err instanceof Error ? err.message : err,
    });
    recordCipherFontLoadSummary({
      platform: 'web',
      cdnBase,
      totalFaces: 0,
      failedFaces: 1,
    });
    return;
  }

  try {
    let style = document.querySelector('style[data-cipher-fonts]') as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.setAttribute('data-cipher-fonts', 'true');
      document.head.appendChild(style);
    }
    style.textContent = css;
    document.querySelector('link[data-cipher-fonts]')?.remove();
  } catch (err) {
    recordCipherFontLoadFailure({
      kind: 'css_inject',
      platform: 'web',
      message: `Failed to inject cipher font CSS`,
      url: cssUrl,
      detail: err instanceof Error ? err.message : err,
    });
    return;
  }

  const rules = parseFontFaceRulesFromCss(css);
  registerCipherFontRules(rules);
  recordCipherFontLoadSummary({
    platform: 'web',
    cdnBase,
    totalFaces: rules.length,
    failedFaces: 0,
  });
}
